import { parseAttributes } from 'app/components/common/FormattedText/tagParser';
import {
  CustomArrayRule,
  CustomRules,
  ICustomRule,
  ICustomRuleBase,
  ICustomRuleOptions,
  MarkupRule,
  TextOutput,
} from 'app/components/common/FormattedText/types';
import Link from 'app/components/router/Link';
import Sentence, { SentenceAttributes } from 'app/components/uknow/AnswerMarkdown/Sentence';
import Word, { WordAttributes } from 'app/components/uknow/AnswerMarkdown/Word';
import { FIB_EMBEDDING_MARKER } from 'app/constants/enums/QuizType';
import { List } from 'immutable';
import React, { ElementType, ReactNode } from 'react';
import {
  defaultRules,
  inlineRegex,
  outputFor,
  parseInline,
  parserFor,
  ReactNodeOutput,
  sanitizeUrl,
} from 'simple-markdown';

// Matches all non-newline characters, followed by special characters, newlines, URLs, or the end of
// the string (which should match all of the other rules).
const TEXT_MATCH = /^[^\n]+?(?=[^0-9A-Za-z\s\u00c0-\uffff]|\n|\w+:\S|$)/;
// This is the base of the sentence matching regex. This matches a single sentence's worth of tag data
const SENTENCE_REGEX = /(<sentence[^>]*>)[^\S\r\n]*[\r\n]?[^\S\r\n]*/;

const outputElement = (TagName: ElementType): ReactNodeOutput => (node, output, state) => (
  <TagName key={state.key}>{output(node.content, state)}</TagName>
);

/**
 * Helps construct rules by giving them an order and configuring the match logic.
 */
const makeRules = <R extends MarkupRule>(
  entries: Array<[R, ICustomRuleBase, ICustomRuleOptions]>,
): { [I in R]: ICustomRule } => {
  const customRules: Array<[R, ICustomRule]> = entries.map(([rule, { match, parse, react, text }, options], order) => [
    rule,
    {
      match: options.configurable
        ? (source, state, prevCapture) => {
          if (state.ruleSet.has(rule)) {
            return match(source, state, prevCapture);
          } else {
            return null;
          }
        }
        : match,
      order,
      parse,
      react,
      text,
    },
  ]);
  return Object.fromEntries(customRules);
};

// `text` and `br` rules must always be enabled so their regexes can match the entire input, due
// to the way simple-markdown works.
const TextBrCustomRuleOptions: ICustomRuleOptions = {
  configurable: false,
};

/**
 * Handles basic content for converting to text..
 */
const defaultText: TextOutput = (node, output, state) => {
  return output(node.content, state);
};

// Warning: Do not use any of the `react` output functions provided by `simple-markdown`'s
// `defaultRules`, because they may not match the behavior of the current version of React, which
// can cause bizarre behavior.
export const customRules: CustomRules = makeRules([
  [
    MarkupRule.Sentence,
    {
      // The insane regex at the end matches any whitespace but not more than 1 \r or \n
      match: inlineRegex(new RegExp(`^(${SENTENCE_REGEX.source})+`)),
      parse: (capture, parse, state) => {
        // Match any number of sentences from the captured sentence group.
        const matches = Array.from(capture[0].matchAll(new RegExp(SENTENCE_REGEX.source, 'g')));
        const sentences = matches.map(match =>
          // Parse the sentence attributes out of each matching sentence
          parseAttributes(match[1], SentenceAttributes.type),
        );
        const props = { sentences: List(sentences) };
        return {
          props,
          input: capture[0],
        };
      },
      react: (node, output, state): ReactNode =>
        (node.props ? <Sentence {...node.props} key={state.key} /> : node.input),
      text: null,
    },
    { configurable: true },
  ],
  [
    MarkupRule.Word,
    {
      match: inlineRegex(/^<word[^>]*>/),
      parse: (capture, parse, state) => {
        const props = parseAttributes(capture[0], WordAttributes.type);
        return {
          props,
          input: capture[0],
        };
      },
      react: (node, output, state): ReactNode => (node.props ? <Word {...node.props} key={state.key} /> : node.input),
      text: null,
    },
    { configurable: true },
  ],
  [
    MarkupRule.Ruby,
    {
      // captures the format {振り仮名|ふりがな}. Any character is allowed on either side,
      // except for: newlines, brackets or the delimiter.
      match: inlineRegex(/^\{([^\n}{|]+)\|([^\n}{|]+)}/),

      // recursively parse the word capture and annotation capture
      parse: (capture, parse, state) => ({
        wordContent: parseInline(parse, capture[1], state),
        annotationContent: parseInline(parse, capture[2], state),
      }),

      react: (node, output, state) => {
        const wordContentOutput = output(node.wordContent, state);

        // wrap raw text nodes in span to prevent problems with google translate
        // https://github.com/facebook/react/issues/11538
        const wrapped = typeof wordContentOutput === 'string' ? <span>{wordContentOutput}</span> : wordContentOutput;

        return (
          <ruby key={state.key}>
            {wrapped}
            <rt>{output(node.annotationContent, state)}</rt>
          </ruby>
        );
      },
      text: (node, ouput, state) => {
        // For rendering plain text, remove the annotation.
        return ouput(node.wordContent, state);
      },
    },
    { configurable: true },
  ],
  // Automatically convert URLs to <a> tags
  [
    MarkupRule.Url,
    {
      match: defaultRules.url.match,
      // Parse returns an AST with field type of `link`. For this reason,
      // defer to `link` for constructing output.
      parse: defaultRules.url.parse,
      react: null,
      text: null,
    },
    { configurable: true },
  ],
  // custom [link](http://url/) to open in new ta
  [
    MarkupRule.Link,
    {
      match: defaultRules.link.match,
      parse: defaultRules.link.parse,
      react: (node, output, state) => (
        <Link key={state.key} to={sanitizeUrl(node.target)} target="_blank" className={state.classNames.link}>
          {output(node.content, state)}
        </Link>
      ),
      text: defaultText,
    },
    { configurable: true },
  ],
  // Custom rules for chat-style bold/italic markup:
  // *bold
  [
    MarkupRule.Bold,
    {
      parse: defaultRules.strong.parse,
      match: inlineRegex(/^\*([\s\S]+?)\*(?!\*)/),
      react: outputElement('strong'),
      text: defaultText,
    },
    { configurable: true },
  ],
  // _italic
  [
    MarkupRule.Italic,
    {
      parse: defaultRules.em.parse,
      match: inlineRegex(/^_([\s\S]+?)_(?!_)/),
      react: outputElement('em'),
      text: defaultText,
    },
    { configurable: true },
  ],
  // ~~strikethrough~
  [
    MarkupRule.Strikethrough,
    {
      parse: defaultRules.del.parse,
      match: defaultRules.del.match,
      react: outputElement('del'),
      text: defaultText,
    },
    { configurable: true },
  ],
  // `inline code
  [
    MarkupRule.InlineCode,
    {
      match: defaultRules.inlineCode.match,
      parse: defaultRules.inlineCode.parse,
      // No markup is allowed inside code blocks, so don't run it through the output function.
      react: (node, output, state) => <code key={state.key}>{node.content}</code>,
      text: (node, strip, state): string => node.content,
    },
    { configurable: true },
  ],
  // Convert <quiz> markers to blanks
  [
    MarkupRule.QuizBlank,
    {
      match: (source, state) => (source.startsWith(FIB_EMBEDDING_MARKER) ? [FIB_EMBEDDING_MARKER] : null),

      // Ignore matched content.
      parse: () => ({}),

      react: (node, output, state) => {
        let quizBlankLabel;
        if (state.quizBlankLabels && state.quizBlankIndex < state.quizBlankLabels.length) {
          quizBlankLabel = state.quizBlankLabels[state.quizBlankIndex];
        }

        // eslint-disable-next-line no-param-reassign
        state.quizBlankIndex += 1;

        return (
          <span
            key={state.key}
            className={quizBlankLabel ? state.classNames.quizBlankWithLabel : state.classNames.quizBlank}
            data-quiz-blank
          >
            {quizBlankLabel}
          </span>
        );
      },

      text: null,
    },
    { configurable: true },
  ],
  // Convert newlines to <br>
  [
    MarkupRule.Break,
    {
      parse: defaultRules.br.parse,
      match: (source, state) => /^\n/.exec(source),
      react: (node, output, state) => {
        if (state.ruleSet.has(MarkupRule.Break)) {
          return <br key={state.key} />;
        } else {
          return '\n';
        }
      },
      text: (node, output, state) => {
        if (state.ruleSet.has(MarkupRule.Break)) {
          return '';
        } else {
          return '\n';
        }
      },
    },
    TextBrCustomRuleOptions,
  ],
  // Parse plain text, but don't eat newlines that aren't preceded by two spaces or another newline
  // like defaultRules.text does, because we want to allow single newlines to be converted to <br>
  // tags without requiring two spaces at the end of the line like Markdown.
  //
  // NOTE: With simple-markdown the `text` rule must match anything that none of your other rules
  // should match or else parsing might fail with an error that no rules matched the text. This
  // means that you might have to update the regex for `text` any time you update another rule,
  // making it potentially difficult to maintain
  [
    MarkupRule.Text,
    {
      ...defaultRules.text,
      match: inlineRegex(TEXT_MATCH),
      react: (node, output, state): ReactNode => node.content,
      text: (node, strip): string => node.content,
    },
    TextBrCustomRuleOptions,
  ],
  // NOTE: the br and text rules should always both appear at the end of any parsers made with these
  // rules, so the br rule can "eat" all newlines and the text rule can eat all other characters. If
  // these rules aren't included, then there may be no rules to fall back on and parsing may fail.
]);

// Some extensions modify the DOM outside of React which
// cause issues when rerendering raw text nodes. We work around this by
// wrapping raw text nodes that are in arrays with a `<span>`.
//
// This is essentially the `output` function being passed into the `react`
// function in the rules. It is important that any result of `{output(...)}` be
// wrapped by itself in an element. Otherwise, there is a possibility of an
// error happening when the result of `output` turns from plain text into
// something else.
//
// Relevant issues:
// https://guoyunhe.me/en/2019/06/02/fix-react-issues-with-google-translate/
// https://github.com/facebook/react/issues/11538
const ArrayParser: CustomArrayRule = {
  react: (nodes, output, state) => {
    const reactNodes: ReactNode[] = [];
    const initialKey = state.key;
    let lastIsString = false;

    // this merges consecutive text nodes into a single node
    // this follows the existing `reactFor` behavior
    for (let i = 0; i < nodes.length; ++i) {
      // we modify this instead of changing immutably because
      // some output functions rely on state being mutable
      // eslint-disable-next-line no-param-reassign
      state.key = i;
      const reactNode = output(nodes[i], state);
      const isString = typeof reactNode === 'string';

      if (lastIsString && typeof reactNode === 'string') {
        reactNodes[reactNodes.length - 1] += reactNode;
      } else {
        reactNodes.push(reactNode);
      }
      lastIsString = isString;
    }
    // eslint-disable-next-line no-param-reassign
    state.key = initialKey;

    if (reactNodes.length > 1) {
      // if we have multiple nodes, wrap text nodes in `<span>`
      return reactNodes.map((reactNode, index) => {
        if (typeof reactNode === 'string') {
          return <span key={`text-${index}`}>{reactNode}</span>;
        } else {
          return reactNode;
        }
      });
    } else if (reactNodes.length === 1) {
      return reactNodes[0];
    } else {
      return null;
    }
  },
  text: (nodes, output, state) => {
    return nodes.map(node => output(node, state)).join('');
  },
};

const rules = {
  // Array is a special rule that can be used to handle arrays of content.
  Array: ArrayParser,
  ...customRules,
};

export const markupParser = parserFor(rules);
export const reactRenderer = outputFor(rules, 'react');
export const textRenderer = outputFor(rules, 'text');
